import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import get from 'lodash/get';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotateLeft, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import { FormattedMessage, FormattedDate, defineMessages, useIntl } from 'react-intl';

import { IsChallenger } from 'Redux/ducks/split-test';
import { fetchShowcase } from 'Redux/ducks/algolia-calls';
import env from 'Env';
import imgixConfig from 'Config/imgix';
import useViewport from 'Hooks/useViewport';
import NavBar from 'Containers/NavBar/NavBar';
import Meta from 'Components/Utility/SEO/Meta';

import 'Components/Showcase/Showcase.scss';

export default function Showcase() {
  const [showcaseGames, setShowcaseGames] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeGameNumber, setActiveGameNumber] = useState(0);
  const [sliderPosition, setSliderPosition] = useState(0);
  const [interacted, setInteracted] = useState(false);

  const { formatMessage } = useIntl();

  const messages = defineMessages({
    meta_title: {
      id: 'showcase.title',
      defaultMessage: 'Showcase | Fanatical',
    },
    meta_description: {
      id: 'showcase.meta_description',
      defaultMessage: 'Showcasing the best new and upcoming Steam Games',
    },
  });

  const {
    width,
    isAboveMd,
    isAboveLg,
    isAboveXl,
  } = useViewport();

  // These are hardcoded for consistency and shouldn't be changed.
  let sliderGap = 16; // pixels (1rem)
  if (isAboveMd) sliderGap = 24; // pixels (1.5rem)
  if (isAboveXl) sliderGap = 32; // pixels (2rem)

  let slideWidth = 200;
  if (isAboveLg) slideWidth = 250;
  if (isAboveXl) slideWidth = 300;

  const loadNext = () => {
    setSliderPosition(sliderPosition - (slideWidth + sliderGap));
    setActiveGameNumber(activeGameNumber + 1);
    setInteracted(true);
  };

  const loadPrevious = () => {
    setSliderPosition(sliderPosition + (slideWidth + sliderGap));
    setActiveGameNumber(activeGameNumber - 1);
    setInteracted(true);
  };

  const loadFirst = () => {
    setSliderPosition(0);
    setActiveGameNumber(0);
    setInteracted(false);
  };

  const loadSelected = (selected) => {
    // const numerator = selected + 1;
    setSliderPosition(-(slideWidth * selected) - (sliderGap * selected));
    setActiveGameNumber(selected);
    setInteracted(true);
  };

  // Get Google Font
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=PT+Sans+Narrow:wght@400;700&display=swap';
    link.type = 'text/css';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  });

  // Get showcase games
  useEffect(() => {
    const fetchHits = async () => {
      const results = await fetchShowcase();

      setShowcaseGames(results.hits);
    };

    fetchHits();
  }, []);

  // Get showcase games
  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false);
    }, 3000);
  }, []);

  // Animate
  useEffect(() => {
    let timer;
    if (!isLoading && !interacted && activeGameNumber + 1 !== showcaseGames.length) {
      timer = setTimeout(() => {
        setSliderPosition(sliderPosition - (slideWidth + sliderGap));
        setActiveGameNumber(activeGameNumber + 1);
      }, 6000);
    }

    // Clear timeout if the component is unmounted
    return () => clearTimeout(timer);
  }, [
    interacted,
    activeGameNumber,
    showcaseGames,
    sliderGap,
    sliderPosition,
    slideWidth,
    isLoading,
  ]);

  const activeGame = showcaseGames[activeGameNumber];
  const gameName = get(activeGame, 'name', '');
  const raleaseDate = get(activeGame, 'release_date', '');
  const slug = get(activeGame, 'slug', '');

  let carouselLeftMargin = (32) / 2;
  if (isAboveMd) carouselLeftMargin = (width - 944) / 2;
  if (isAboveLg) carouselLeftMargin = (width - 1124) / 2;
  if (isAboveXl) carouselLeftMargin = (width - 1324) / 2;

  const outNow = raleaseDate * 1000 < Date.now();

  return (
    <main className={classNames('Showcase', { 'Showcase--test': IsChallenger('ShowcaseDarkTheme') })}>
      <Meta
        title={formatMessage(messages.meta_title)}
        url="showcase"
        hits={showcaseGames}
        description={formatMessage(messages.meta_description)}
        pageType="CollectionPage"
      />

      {isLoading &&
        <section className="Showcase__loading">
          <div className="Showcase__loading__icon">
            <img src="https://cdn.fanatical.com/production/brand/fanatical-icon-white.svg" alt="Loading Page" />
          </div>
        </section>
      }

      <section className={classNames('Showcase__main', { 'Showcase__main--hidden': isLoading })}>
        <div className="Showcase__main__foreground">
          <NavBar clearNav />

          <div className="Showcase__main__foreground__middle container">
            <div className="Showcase__main__foreground__middle__content">
              <div className="Showcase__main__foreground__middle__content__name">
                {gameName}
              </div>
              <div className="Showcase__main__foreground__middle__content__date">
                {outNow ? 'Released' : 'Coming'} <FormattedDate value={raleaseDate * 1000} year="numeric" month="long" day="2-digit" />
              </div>
              <Link to={`/showcase/${slug}`} className="Showcase__main__foreground__middle__content__button" onMouseEnter={() => setInteracted(true)}>
                <FormattedMessage id="showcase.button.find-out-more" defaultMessage="Find out more" />
              </Link>
            </div>
          </div>

          <div className="Showcase__main__foreground__bottom">
            <div className="Showcase__carouselHeading container">
              <div className="Showcase__carouselHeading__heading">
                <FormattedMessage id="showcase.carousel.heading" defaultMessage="Showcasing the best New & Upcoming Releases" />
              </div>
              <div className="Showcase__carouselHeading__buttons">
                <button
                  disabled={activeGameNumber === 0}
                  className={classNames('Showcase__carouselHeading__buttons__button', { 'Showcase__carouselHeading__buttons__button--hidden': activeGameNumber + 1 !== showcaseGames.length })}
                  onClick={() => loadFirst()}
                >
                  <FontAwesomeIcon icon={faRotateLeft} title="Reset carousel" />
                </button>

                <button
                  disabled={activeGameNumber === 0}
                  className="Showcase__carouselHeading__buttons__button"
                  onClick={() => loadPrevious()}
                >
                  <FontAwesomeIcon icon={faChevronLeft} title="Previous" />
                </button>

                <button
                  disabled={activeGameNumber + 1 === showcaseGames.length}
                  className="Showcase__carouselHeading__buttons__button"
                  onClick={() => loadNext()}
                >
                  <FontAwesomeIcon icon={faChevronRight} title="Next" />
                </button>
              </div>
            </div>

            <div className="Showcase__carousel">
              <div className="Showcase__carousel__slides" style={{ left: `${sliderPosition}px` }}>
                {showcaseGames && showcaseGames.map((game, i) =>
                  <div className={`Showcase__carousel__slide Showcase__carousel__slide__${i}`} style={{ width: slideWidth, marginLeft: i === 0 ? carouselLeftMargin : 0, marginRight: i + 1 === showcaseGames.length ? carouselLeftMargin : 0 }}>
                    <button
                      className="Showcase__carousel__slide__card"
                      onClick={() => { loadSelected(i); }}
                    >
                      {i === activeGameNumber &&
                        <div className="Showcase__carousel__slide__card__active" />
                      }
                      {!interacted && i === activeGameNumber &&
                        <div className="Showcase__carousel__slide__card__progress">
                          <div className="Showcase__carousel__slide__card__progress__background">
                            <div className="Showcase__carousel__slide__card__progress__background__fill" />
                          </div>
                        </div>
                      }
                      <img
                        srcSet={`
                          ${env.cdnUrl}/product/original/${game.cover}?${imgixConfig}&q=75&w=1920&h=1080&fit=crop 1920w,
                          ${env.cdnUrl}/product/original/${game.cover}?${imgixConfig}&q=75&w=1280&h=720&fit=crop 1280w,
                          ${env.cdnUrl}/product/original/${game.cover}?${imgixConfig}&q=75&w=1130&h=636&fit=crop 1130w,
                          ${env.cdnUrl}/product/original/${game.cover}?${imgixConfig}&q=75&w=960&h=540&fit=crop 960w,
                          ${env.cdnUrl}/product/original/${game.cover}?${imgixConfig}&q=75&w=740&h=416&fit=crop 740w,
                          ${env.cdnUrl}/product/original/${game.cover}?${imgixConfig}&q=75&w=640&h=360&fit=crop 640w,
                          ${env.cdnUrl}/product/original/${game.cover}?${imgixConfig}&q=75&w=480&h=270&fit=crop 480w,
                          ${env.cdnUrl}/product/original/${game.cover}?${imgixConfig}&q=75&w=384&h=216&fit=crop 384w
                        `}
                        sizes="(min-width: 1520px) 300px, (min-width: 1200px) 250px, 200px"
                        src={`${env.cdnUrl}/product/original/${game.cover}?${imgixConfig}&q=75&w=1920&h=1080&fit=crop 1920w`}
                        alt={game.name}
                      />
                    </button>
                  </div>,
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="Showcase__main__topGradient" />
        <div className="Showcase__main__bottomGradient" />
        <div className="Showcase__main__overlay" />
        <div className="Showcase__main__background">
          {showcaseGames && showcaseGames.map((game, i) => {
            const screenshots = get(game, 'screenshots', []);
            let backgroundSrc = get(screenshots, [0], '');
            if (game.slug === 'indiana-jones-and-the-great-circle') {
              backgroundSrc = get(screenshots, [1], '');
            }

            if (i !== activeGameNumber && i !== activeGameNumber + 1) return false;
            return (
              <img
                srcSet={`
                  ${env.cdnUrl}/product/original/${backgroundSrc}?${imgixConfig}&q=25&w=1920&h=1080&fit=crop 1920w,
                  ${env.cdnUrl}/product/original/${backgroundSrc}?${imgixConfig}&q=25&w=1280&h=720&fit=crop 1280w,
                  ${env.cdnUrl}/product/original/${backgroundSrc}?${imgixConfig}&q=25&w=1130&h=636&fit=crop 1130w,
                  ${env.cdnUrl}/product/original/${backgroundSrc}?${imgixConfig}&q=25&w=960&h=540&fit=crop 960w,
                  ${env.cdnUrl}/product/original/${backgroundSrc}?${imgixConfig}&q=25&w=740&h=416&fit=crop 740w,
                  ${env.cdnUrl}/product/original/${backgroundSrc}?${imgixConfig}&q=25&w=640&h=360&fit=crop 640w,
                  ${env.cdnUrl}/product/original/${backgroundSrc}?${imgixConfig}&q=25&w=480&h=270&fit=crop 480w,
                  ${env.cdnUrl}/product/original/${backgroundSrc}?${imgixConfig}&q=25&w=384&h=216&fit=crop 384w
                `}
                src={`${env.cdnUrl}/product/original/${backgroundSrc}?${imgixConfig}&q=25&w=1920&h=1080&fit=crop 1920w`}
                sizes="100vw"
                alt=""
                className={classNames('Showcase__main__background__img', { 'Showcase__main__background__img--active': i === activeGameNumber })}
              />
            );
          })}
        </div>
      </section>
    </main>
  );
}
